/**
 * Global confirmation store. `runTool` awaits `requestConfirmation()`; the <ConfirmationHost/> renders
 * the modal for the head of the queue and resolves it. Framework-free so it works outside React too.
 */
export interface ConfirmationRequest {
  tool: string;
  label?: string;
  summary: string;
  expiresAt?: string;
  input?: Record<string, unknown>;
}

export interface PendingConfirmation extends ConfirmationRequest {
  id: number;
  resolve: (ok: boolean) => void;
}

type Listener = (queue: PendingConfirmation[]) => void;

let queue: PendingConfirmation[] = [];
let seq = 0;
const listeners = new Set<Listener>();

function emit() {
  for (const l of listeners) l(queue);
}

export function requestConfirmation(req: ConfirmationRequest): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    const id = ++seq;
    queue = [
      ...queue,
      {
        ...req,
        id,
        resolve: (ok) => {
          queue = queue.filter((q) => q.id !== id);
          emit();
          resolve(ok);
        },
      },
    ];
    emit();
  });
}

export function subscribeConfirmations(l: Listener): () => void {
  listeners.add(l);
  l(queue);
  return () => {
    listeners.delete(l);
  };
}

/** Test helper. */
export function resetConfirmations(): void {
  for (const q of queue) q.resolve(false);
  queue = [];
  emit();
}

/** Heuristic for styling the confirm button as destructive. */
export function isDestructiveTool(tool: string): boolean {
  return /(delete|remove|disconnect|revoke|retire|archive|kill|graveyard|pause|cancel)/i.test(tool);
}
